import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpContextToken,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, finalize, shareReplay, switchMap, throwError, timer, timeout } from 'rxjs';

import { environment } from '../../../environments/environment';

export const SKIP_AUTH_REFRESH = new HttpContextToken<boolean>(() => false);

@Injectable()
export class AuthExpiredInterceptor implements HttpInterceptor {
  private readonly http = inject(HttpClient);
  private refreshInFlight$: Observable<unknown> | null = null;

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(req).pipe(
      catchError((error: unknown) => {
        if (!shouldAttemptRefresh(req, error)) {
          return throwError(() => error);
        }

        return this.refreshSession().pipe(
          switchMap(() => timer(250)),
          switchMap(() => next.handle(withCredentialedFetch(req))),
          catchError((refreshError: unknown) => throwError(() => refreshError)),
        );
      }),
    );
  }

  private refreshSession(): Observable<unknown> {
    if (!this.refreshInFlight$) {
      this.refreshInFlight$ = this.http.post(`${environment.apiGatewayUrl}/api/v1/user/refresh`, {}, {
        responseType: 'text',
        credentials: 'include',
      }).pipe(
        timeout(8000),
        finalize(() => {
          this.refreshInFlight$ = null;
        }),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
    }

    return this.refreshInFlight$;
  }
}

function withCredentialedFetch(req: HttpRequest<unknown>): HttpRequest<unknown> {
  return req.credentials === 'include'
    ? req
    : req.clone({ withCredentials: false, credentials: 'include' });
}

function shouldAttemptRefresh(req: HttpRequest<unknown>, error: unknown): boolean {
    if (req.headers.has('X-Financial-Confirmation')) return false;
  return (
    typeof window !== 'undefined' &&
    !req.context.get(SKIP_AUTH_REFRESH) &&
    !isAuthBypassEndpoint(req) &&
    isAuthExpired(error)
  );
}

function isAuthBypassEndpoint(req: HttpRequest<unknown>): boolean {
  const url = req.url;
  return (
    url.includes('/api/v1/user/login') ||
    url.includes('/api/v1/user/pre-authenticate') ||
    url.includes('/api/v1/user/verify') ||
    url.includes('/api/v1/user/send-token') ||
    url.includes('/api/v1/user/refresh') ||
    url.includes('/api/v1/user/logout')
  );
}

export function isAuthExpired(error: unknown): boolean {
  if (!(error instanceof HttpErrorResponse)) {
    return false;
  }

  const errorCode = String(error.error?.error ?? error.error?.errorCode ?? '').toUpperCase();
  const message = String(error.error?.message ?? '').toLowerCase();
  const url = String(error.url ?? '').toLowerCase();
  const authEndpoint = url.includes('/api/v1/user/');
  const authVerifyEndpoint = url.includes('/api/v1/user/auth-verify');

  return (
    error.status === 401 ||
    error.status === 419 ||
    (errorCode === 'AUTHORIZATION_FAILED' && authVerifyEndpoint) ||
    (error.status === 403 && authEndpoint && (message.includes('authorization failed') || message.includes('no cookies')))
  );
}
